/**
 * FluxAccounts sign-in screen. Preserves the requested planner location as the return target,
 * starts login through AuthContext, and restores the button with an error toast if navigation setup
 * fails.
 *
 * @module apps/web/src/pages/AuthPage
 */

import { useState } from 'react';
import { KeyRound, Loader2, Orbit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { SignInNotice } from '@/components/auth/SignInNotice';
import { readSignInFailure } from '@/lib/signInNotice';

/**
 * FluxAccounts sign-in screen. Preserves the requested planner location as the return target,
 * starts login through AuthContext, and restores the button with an error toast if navigation setup
 * fails.
 */
export function AuthPage() {
  const { loginWithSaId, authError } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSaIdLogin = async () => {
    setIsSubmitting(true);
    try {
      const returnTo = window.location.pathname.startsWith('/auth')
        ? '/week'
        : `${window.location.pathname || '/week'}${window.location.search}${window.location.hash}`;
      await loginWithSaId(returnTo);
    } catch (error) {
      toast({
        title: 'Could not open FluxAccounts',
        description:
          error instanceof Error ? error.message : 'Check your connection and try again.',
        variant: 'destructive',
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-background flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-2">
          <span className="bg-primary text-primary-foreground flex h-10 w-10 items-center justify-center rounded-lg shadow-[0_0_34px_hsl(var(--primary)/0.34)]">
            <Orbit className="h-6 w-6" />
          </span>
          <div>
            <h1 className="text-2xl font-bold leading-tight">Fluxability</h1>
            <p className="text-muted-foreground text-xs">One account, many workspaces.</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {authError ? 'Could not complete sign-in' : 'Sign in to Fluxability'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* The notice re-reads the stored failure whenever the callback reports a new one; the
                plain message is the fallback when session storage is unavailable. */}
            {authError && !readSignInFailure() ? (
              <p role="alert" className="text-destructive text-sm">
                {authError}
              </p>
            ) : (
              <SignInNotice key={authError ?? 'none'} />
            )}
            <p className="text-muted-foreground text-sm">
              Use FluxAccounts to open your planner, teams, and settings. Existing Mail-linked
              accounts are matched after FluxAccounts verifies your email.
            </p>
            <Button
              type="button"
              className="w-full"
              disabled={isSubmitting}
              onClick={handleSaIdLogin}
            >
              {isSubmitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <KeyRound className="mr-2 h-4 w-4" />
              )}
              {isSubmitting
                ? 'Opening FluxAccounts...'
                : authError
                  ? 'Try signing in again'
                  : 'Continue with FluxAccounts'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
